import React, { useEffect, useMemo, useState } from "react";
import "./DateRangeFilter.scss";

const DEFAULT_PRESETS = [
  "Today",
  "Yesterday",
  "Last 7 Days",
  "Last 15 Days",
  "Last 30 Days",
  "Last 90 Days",
  "Last Year",
  "This Year",
  "All Time",
  "Custom",
];

const DAY_LABELS = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"];
const MONTH_LABELS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function startOfDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function endOfDay(date) {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
}

function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function addMonths(date, months) {
  const d = new Date(date);
  d.setMonth(d.getMonth() + months);
  return d;
}

function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function endOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

function startOfQuarter(date) {
  return new Date(date.getFullYear(), Math.floor(date.getMonth() / 3) * 3, 1);
}

function endOfQuarter(date) {
  return new Date(date.getFullYear(), Math.floor(date.getMonth() / 3) * 3 + 3, 0);
}

function toIsoDate(date) {
  if (!date) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function parseIsoDate(value) {
  if (!value) return null;
  const [year, month, day] = String(value).split("-").map(Number);
  if (!year || !month || !day) return null;
  return startOfDay(new Date(year, month - 1, day));
}

function formatDisplayDate(date) {
  if (!date) return "";
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function sameDay(a, b) {
  return Boolean(a && b && toIsoDate(a) === toIsoDate(b));
}

function normalizePreset(preset) {
  return preset === "Custom Range" ? "Custom" : preset;
}

function observedHolidayDate(date) {
  const day = date.getDay();
  if (day === 6) return addDays(date, 2);
  if (day === 0) return addDays(date, 1);
  return date;
}

function firstMonday(year, month) {
  const date = new Date(year, month, 1);
  while (date.getDay() !== 1) {
    date.setDate(date.getDate() + 1);
  }
  return date;
}

function lastMonday(year, month) {
  const date = new Date(year, month + 1, 0);
  while (date.getDay() !== 1) {
    date.setDate(date.getDate() - 1);
  }
  return date;
}

function getEasterSunday(year) {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31) - 1;
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month, day);
}

function getUkHolidaysForYear(year) {
  const easter = getEasterSunday(year);
  const christmas = new Date(year, 11, 25);
  const boxingDay = new Date(year, 11, 26);
  const observedChristmas = observedHolidayDate(christmas);
  const observedBoxingDay =
    toIsoDate(observedChristmas) === toIsoDate(observedHolidayDate(boxingDay))
      ? addDays(observedHolidayDate(boxingDay), 1)
      : observedHolidayDate(boxingDay);

  return [
    { date: observedHolidayDate(new Date(year, 0, 1)), name: "New Year's Day" },
    { date: addDays(easter, -2), name: "Good Friday" },
    { date: addDays(easter, 1), name: "Easter Monday" },
    { date: firstMonday(year, 4), name: "Early May Bank Holiday" },
    { date: lastMonday(year, 4), name: "Spring Bank Holiday" },
    { date: lastMonday(year, 7), name: "Summer Bank Holiday" },
    { date: observedChristmas, name: "Christmas Day" },
    { date: observedBoxingDay, name: "Boxing Day" },
  ];
}

function getHolidaysBetween(startDate, endDate, countryCode) {
  if (!startDate || !endDate || String(countryCode) !== "+44") return [];
  const start = startOfDay(startDate);
  const end = endOfDay(endDate);
  const years = [];
  for (let year = start.getFullYear(); year <= end.getFullYear(); year += 1) {
    years.push(year);
  }

  return years
    .flatMap(getUkHolidaysForYear)
    .filter((holiday) => holiday.date >= start && holiday.date <= end)
    .sort((a, b) => a.date.getTime() - b.date.getTime());
}

function getPresetRange(preset) {
  const today = startOfDay(new Date());
  const yesterday = addDays(today, -1);
  const thisWeekStart = addDays(today, -today.getDay());
  const lastWeekStart = addDays(thisWeekStart, -7);
  const thisMonthStart = startOfMonth(today);
  const lastMonth = addMonths(today, -1);
  const thisQuarterStart = startOfQuarter(today);
  const lastQuarterDate = addMonths(thisQuarterStart, -3);

  switch (preset) {
    case "Today":
      return { startDate: today, endDate: today };
    case "Yesterday":
      return { startDate: yesterday, endDate: yesterday };
    case "This Week":
      return { startDate: thisWeekStart, endDate: today };
    case "Last Week":
      return { startDate: lastWeekStart, endDate: addDays(thisWeekStart, -1) };
    case "Last 7 Days":
      return { startDate: addDays(today, -7), endDate: today };
    case "Last 15 Days":
      return { startDate: addDays(today, -15), endDate: today };
    case "Last 30 Days":
      return { startDate: addDays(today, -30), endDate: today };
    case "Last 90 Days":
      return { startDate: addDays(today, -90), endDate: today };
    case "This Month":
      return { startDate: thisMonthStart, endDate: today };
    case "Last Month":
      return { startDate: startOfMonth(lastMonth), endDate: endOfMonth(lastMonth) };
    case "This Quarter":
      return { startDate: thisQuarterStart, endDate: today };
    case "Last Quarter":
      return { startDate: startOfQuarter(lastQuarterDate), endDate: endOfQuarter(lastQuarterDate) };
    case "This Year":
      return { startDate: new Date(today.getFullYear(), 0, 1), endDate: today };
    case "Last Year":
      return {
        startDate: new Date(today.getFullYear() - 1, 0, 1),
        endDate: new Date(today.getFullYear() - 1, 11, 31),
      };
    default:
      return { startDate: null, endDate: null };
  }
}

function getCalendarCells(monthDate) {
  const first = startOfMonth(monthDate);
  const firstCell = addDays(first, -first.getDay());
  return Array.from({ length: 42 }, (_, index) => addDays(firstCell, index));
}

function DateMonth({ monthDate, draftStart, draftEnd, onSelectDate, holidayDates }) {
  const cells = getCalendarCells(monthDate);
  const month = monthDate.getMonth();

  return (
    <div className="aq-date-range-modal__month">
      <h3>{`${MONTH_LABELS[month]} ${monthDate.getFullYear()}`}</h3>
      <div className="aq-date-range-modal__weekdays">
        {DAY_LABELS.map((label, index) => (
          <span
            key={label}
            className={index === 0 || index === 6 ? "is-weekend" : ""}
          >
            {label}
          </span>
        ))}
      </div>
      <div className="aq-date-range-modal__calendar">
        {cells.map((date) => {
          const inMonth = date.getMonth() === month;
          const isWeekend = date.getDay() === 0 || date.getDay() === 6;
          const isHoliday = holidayDates.has(toIsoDate(date));
          const selectedStart = sameDay(date, draftStart);
          const selectedEnd = sameDay(date, draftEnd);
          const inRange =
            draftStart &&
            draftEnd &&
            startOfDay(date).getTime() >= startOfDay(draftStart).getTime() &&
            endOfDay(date).getTime() <= endOfDay(draftEnd).getTime();

          return (
            <button
              type="button"
              key={toIsoDate(date)}
              className={[
                "aq-date-range-modal__day",
                inMonth ? "" : "is-muted",
                isWeekend ? "is-weekend" : "",
                isHoliday ? "is-holiday" : "",
                inRange ? "is-in-range" : "",
                selectedStart || selectedEnd ? "is-selected" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              onClick={() => onSelectDate(date)}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const DateRangeFilter = ({
  value,
  onChange,
  placeholder = "Select date range",
  countryCode = "+44",
  presetOptions = DEFAULT_PRESETS,
  compact = false,
  hideTrigger = false,
  open: openProp,
  onOpenChange,
}) => {
  const selectedPreset = normalizePreset(value?.preset || value?.dateRange || "Custom");
  const selectedStartValue = value?.startDate || value?.dateFrom || "";
  const selectedEndValue = value?.endDate || value?.dateTo || "";
  const selectedStart = parseIsoDate(selectedStartValue);
  const selectedEnd = parseIsoDate(selectedEndValue);
  const presetRange = useMemo(() => getPresetRange(selectedPreset), [selectedPreset]);
  const effectiveStart = selectedStart || presetRange.startDate;
  const effectiveEnd = selectedEnd || presetRange.endDate;

  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = openProp !== undefined;
  const open = isControlled ? !!openProp : internalOpen;
  const setOpen = (next) => {
    if (!isControlled) setInternalOpen(next);
    onOpenChange?.(next);
  };
  const [activePreset, setActivePreset] = useState(selectedPreset);
  const [draftStart, setDraftStart] = useState(effectiveStart);
  const [draftEnd, setDraftEnd] = useState(effectiveEnd);
  const [viewMonth, setViewMonth] = useState(startOfMonth(effectiveStart || new Date()));
  const selectedHolidays = useMemo(
    () => getHolidaysBetween(draftStart, draftEnd, countryCode),
    [countryCode, draftEnd, draftStart],
  );
  const holidayDates = useMemo(
    () => new Set(selectedHolidays.map((holiday) => toIsoDate(holiday.date))),
    [selectedHolidays],
  );

  useEffect(() => {
    if (!open) return;
    const range = getPresetRange(selectedPreset);
    const nextStart = parseIsoDate(selectedStartValue) || range.startDate;
    const nextEnd = parseIsoDate(selectedEndValue) || range.endDate;
    setActivePreset(selectedPreset || "Custom");
    setDraftStart(nextStart);
    setDraftEnd(nextEnd);
    setViewMonth(startOfMonth(nextStart || new Date()));
  }, [open, selectedEndValue, selectedPreset, selectedStartValue]);

  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        setOpen(false);
      }
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, [open]);

  const displayValue =
    selectedPreset === "All Time"
      ? "All Time"
      : effectiveStart && effectiveEnd
      ? `${formatDisplayDate(effectiveStart)} - ${formatDisplayDate(effectiveEnd)}`
      : placeholder;

  const selectPreset = (preset) => {
    setActivePreset(preset);
    if (preset === "All Time") {
      setDraftStart(null);
      setDraftEnd(null);
      return;
    }
    if (normalizePreset(preset) === "Custom") return;
    const range = getPresetRange(preset);
    setDraftStart(range.startDate);
    setDraftEnd(range.endDate);
    setViewMonth(startOfMonth(range.startDate || new Date()));
  };

  const selectDate = (date) => {
    const picked = startOfDay(date);
    setActivePreset("Custom");
    if (!draftStart || (draftStart && draftEnd)) {
      setDraftStart(picked);
      setDraftEnd(null);
      return;
    }
    if (picked.getTime() < draftStart.getTime()) {
      setDraftEnd(draftStart);
      setDraftStart(picked);
      return;
    }
    setDraftEnd(picked);
  };

  const applyRange = () => {
    if (activePreset === "All Time") {
      onChange({
        preset: "All Time",
        startDate: "",
        endDate: "",
        label: "All Time",
      });
      setOpen(false);
      return;
    }
    if (!draftStart || !draftEnd) return;
    onChange({
      preset: activePreset,
      startDate: toIsoDate(draftStart),
      endDate: toIsoDate(draftEnd),
      label: `${formatDisplayDate(draftStart)} - ${formatDisplayDate(draftEnd)}`,
    });
    setOpen(false);
  };

  return (
    <div className={`aq-date-range-filter${hideTrigger ? " aq-date-range-filter--ghost" : ""}`}>
      {!hideTrigger ? (
        <button
          type="button"
          className={
            compact
              ? "aq-date-range-filter__input aq-date-range-filter__input--compact"
              : "form-control form-control-sm aq-date-range-filter__input"
          }
          onClick={() => setOpen(true)}
          aria-haspopup="dialog"
          aria-expanded={open}
        >
          <span>{displayValue}</span>
          <i className="fas fa-calendar-alt" aria-hidden="true" />
        </button>
      ) : null}

      {open && (
        <div className="aq-date-range-modal" role="presentation">
          <button
            type="button"
            className="aq-date-range-modal__backdrop"
            aria-label="Close date range selector"
            onClick={() => setOpen(false)}
          />
          <div
            className="aq-date-range-modal__dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="aq-date-range-modal-title"
          >
            <div className="aq-date-range-modal__header">
              <h2 id="aq-date-range-modal-title">Select Date Range</h2>
              <button
                type="button"
                className="aq-date-range-modal__close"
                onClick={() => setOpen(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="aq-date-range-modal__content">
              <aside className="aq-date-range-modal__presets">
                <span>Quick Select</span>
                <div className="aq-date-range-modal__preset-list">
                {presetOptions.map((preset) => (
                  <button
                    type="button"
                    key={preset}
                    className={normalizePreset(activePreset) === normalizePreset(preset) ? "is-active" : ""}
                    onClick={() => selectPreset(preset)}
                  >
                    {preset}
                  </button>
                ))}
                </div>
              </aside>

              <section className="aq-date-range-modal__main">
                <div className="aq-date-range-modal__months-nav">
                  <button
                    type="button"
                    onClick={() => setViewMonth((prev) => addMonths(prev, -1))}
                    aria-label="Previous month"
                  >
                    <i className="fas fa-chevron-left" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewMonth((prev) => addMonths(prev, 1))}
                    aria-label="Next month"
                  >
                    <i className="fas fa-chevron-right" aria-hidden="true" />
                  </button>
                </div>

                <div className="aq-date-range-modal__months">
                  <DateMonth
                    monthDate={viewMonth}
                    draftStart={draftStart}
                    draftEnd={draftEnd}
                    onSelectDate={selectDate}
                    holidayDates={holidayDates}
                  />
                  <DateMonth
                    monthDate={addMonths(viewMonth, 1)}
                    draftStart={draftStart}
                    draftEnd={draftEnd}
                    onSelectDate={selectDate}
                    holidayDates={holidayDates}
                  />
                </div>

                <div className="aq-date-range-modal__holidays">
                  <h3>Holidays</h3>
                  {selectedHolidays.length ? (
                    <ul>
                      {selectedHolidays.map((holiday) => (
                        <li key={`${holiday.name}-${toIsoDate(holiday.date)}`}>
                          <span>{holiday.name}</span>
                          <strong>{formatDisplayDate(holiday.date)}</strong>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p>
                      {String(countryCode) === "+44"
                        ? "No UK holidays in selected range."
                        : "No holiday calendar available for this country code."}
                    </p>
                  )}
                </div>
              </section>
            </div>

            <div className="aq-date-range-modal__footer">
              <button type="button" className="btn btn-link" onClick={() => setOpen(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={activePreset !== "All Time" && (!draftStart || !draftEnd)}
                onClick={applyRange}
              >
                Apply Range
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangeFilter;
